// Per-extension detail page. Route: /extensions/:id where id is the
// sip_account.id (integer, org-scoped). Ports the pbx-portal
// ExtensionDetail pattern: tabbed shell around live agent-side data.
//
// Tabs:
//   Overview     — basics + live SIP registration status
//   Forwarding   — agent /subscribers/:ext/forwarding
//   Voicemail    — agent /subscribers/:ext/voicemail
//   Call History — agent /subscribers/:ext/calls (with time-window filter)
//
// Every tab that hits the agent tolerates null/empty responses (the Rust
// agent may still be stubbing some paths) and renders empty state without
// red-bannering.

import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { fmtDate, fmtDateTime } from '../lib/time.js';
import { useAuth } from '../hooks/useAuth.js';
import { canAdmin } from '../lib/roles.js';
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import { useRevealSecret, RevealSecretDialog, RevealedSecret } from '../components/shared/RevealSecretDialog.jsx';
import { VoicemailSettings } from '../components/shared/VoicemailPanel.jsx';
import VoicemailBadge, { voicemailState } from '../components/shared/VoicemailBadge.jsx';
import { useVoicemailStatus } from '../hooks/useVoicemailStatus.js';
import SetSipPasswordModal from '../components/shared/SetSipPasswordModal.jsx';

const CALL_WINDOWS = [
  { id: '1h',  label: '1 hr',    seconds: 3600 },
  { id: '24h', label: '24 hrs',  seconds: 86400 },
  { id: '3d',  label: '3 days',  seconds: 3 * 86400 },
  { id: '7d',  label: '7 days',  seconds: 7 * 86400 },
  { id: '30d', label: '30 days', seconds: 30 * 86400 },
];


function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
      <div className="mt-0.5 text-sm text-gray-900">{value ?? <span className="text-gray-300">—</span>}</div>
    </div>
  );
}

function Section({ title, subtitle, actions, children }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

function fmtDuration(seconds) {
  if (seconds == null || seconds < 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function ExtensionDetail() {
  const { id } = useParams();
  const { role } = useAuth();
  const canWriteVm = canAdmin(role);
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [secretOpen, setSecretOpen] = useState(false);
  const [callWindow, setCallWindow] = useState('24h');
  // The SIP password goes through the shared acknowledgement gate — every reveal,
  // on every page, warns and is audited. See components/shared/RevealSecretDialog.
  const revealSecret = useRevealSecret('extension-detail');

  // The sip_account carries everything we need to talk to the agent:
  // username (extension number) + sip_server_id → agent_id (from server row).
  const acctQ = useQuery({
    queryKey: ['sip-account', id],
    queryFn:  () => api.get(`/sip-accounts/${id}`),
  });
  const account = acctQ.data;

  // Does the PBX have a mailbox for this extension? Live read, shared with the
  // lists. It's in the header because it decides whether the Voicemail tab's
  // settings apply to anything at all.
  const { entryFor: vmFor } = useVoicemailStatus();
  const vmEntry = vmFor(id);
  const vmState = voicemailState(vmEntry);

  // Look up the SIP server for its agent_id — sip_accounts don't carry
  // it directly (they FK the server, which FKs the agent).
  const serversQ = useQuery({
    queryKey: ['sip-servers'],
    queryFn:  () => api.get('/sip-servers'),
    enabled:  Boolean(account?.sipServerId ?? account?.sip_server_id),
  });
  const server = useMemo(() => {
    const list = Array.isArray(serversQ.data) ? serversQ.data : [];
    const wanted = account?.sipServerId ?? account?.sip_server_id;
    if (!wanted) return null;
    return list.find((s) => s.id === wanted) || null;
  }, [serversQ.data, account]);

  const agentId = server?.agentId || server?.agent_id;
  const ext = account?.username;

  // Live SIP registration status (Overview tab)
  const sipStatusQ = useQuery({
    queryKey: ['ext', id, 'sip-status'],
    queryFn:  () => api.get(`/agents/${agentId}/proxy/extensions/${encodeURIComponent(ext)}/sip-status`),
    enabled:  activeTab === 'overview' && Boolean(agentId && ext),
    refetchInterval: 30_000,
    retry: 1,
  });

  // Forwarding
  const fwdQ = useQuery({
    queryKey: ['ext', id, 'forwarding'],
    queryFn:  () => api.get(`/agents/${agentId}/proxy/subscribers/${encodeURIComponent(ext)}/forwarding`),
    enabled:  activeTab === 'forwarding' && Boolean(agentId && ext),
    retry: 1,
  });

  // Voicemail
  const vmQ = useQuery({
    queryKey: ['ext', id, 'voicemail'],
    queryFn:  () => api.get(`/agents/${agentId}/proxy/subscribers/${encodeURIComponent(ext)}/voicemail`),
    enabled:  activeTab === 'voicemail' && Boolean(agentId && ext),
    retry: 1,
  });

  // Call history
  const callsQ = useQuery({
    queryKey: ['ext', id, 'calls', callWindow],
    queryFn:  () => {
      const win = CALL_WINDOWS.find((w) => w.id === callWindow) || CALL_WINDOWS[1];
      const since = Math.floor(Date.now() / 1000) - win.seconds;
      return api.get(`/agents/${agentId}/proxy/subscribers/${encodeURIComponent(ext)}/calls`, { limit: 50, since });
    },
    enabled:  activeTab === 'callhistory' && Boolean(agentId && ext),
    refetchInterval: 60_000,
    retry: 1,
  });

  if (acctQ.isLoading) return <LoadingSpinner message="Loading extension…" />;
  if (acctQ.isError || !account) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        Extension not found.
      </div>
    );
  }

  const status = String(account.status || 'unknown');
  const subId  = account.subscriberId ?? account.subscriber_id;
  const tabs = [
    { id: 'overview',    label: 'Overview' },
    { id: 'forwarding',  label: 'Forwarding' },
    { id: 'voicemail',   label: 'Voicemail' },
    { id: 'callhistory', label: 'Call History' },
  ];

  return (
    <div className="space-y-6">
      <div className="text-sm text-gray-500">
        <Link to="/extensions" className="hover:text-gray-700 hover:underline">Extensions</Link>
        <span className="mx-1">/</span>
        <span className="text-gray-700">{ext || `#${account.id}`}</span>
      </div>

      {/* Header */}
      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl font-bold text-gray-900">Extension {ext || `#${account.id}`}</h1>
              <StatusBadge status={status === 'active' ? 'active' : 'inactive'} label={status} />
              {/* Whether the PBX has a mailbox for this extension. In the header
                  because it changes what half this page means: with no mailbox,
                  the Voicemail tab's settings apply to nothing. */}
              <VoicemailBadge entry={vmEntry} />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {subId && (
                <>
                  Subscriber{' '}
                  <Link to={`/subscribers/${subId}`} className="text-blue-600 hover:underline">
                    {account?.subscriberName || `#${subId}`}
                  </Link>
                </>
              )}
              {/* No PBX host here. The DID owns the host and this extension
                  inherits it — see its DID page. The server row is still read
                  above, but only to find the agent to talk to. */}
            </p>
          </div>
        </div>
      </div>

      <RevealSecretDialog
        account={revealSecret.pending}
        onConfirm={revealSecret.confirm}
        onCancel={revealSecret.cancel}
        isPending={revealSecret.isPending}
        error={revealSecret.error}
      />

      {/* Tab nav */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap gap-4" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={activeTab === t.id}
              onClick={() => setActiveTab(t.id)}
              className={`inline-flex items-center gap-2 border-b-2 px-1 pb-2 text-sm font-medium transition-colors ${
                activeTab === t.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview tab */}
      {activeTab === 'overview' && (
        <>
          <Section
            title="Basics"
            subtitle="Extension identity as stored in Verso."
            actions={canWriteVm && (
              <div className="flex items-center gap-2">
                {!revealSecret.revealed && (
                  <button
                    onClick={() => revealSecret.request({ id, username: ext })}
                    title="Reads the password live from the PBX. You'll be asked to acknowledge; every reveal is recorded in the audit log."
                    className="rounded-md border border-blue-300 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50"
                  >
                    Show SIP password
                  </button>
                )}
                {/* A Calix router's built-in ATA is given its password by hand and
                    takes no more than 12 characters, so the PBX has to be set to
                    the value the router holds — this is that. */}
                <button
                  onClick={() => setSecretOpen(true)}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  Set SIP password
                </button>
              </div>
            )}
          >
            <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Field label="Extension" value={<span className="font-mono">{ext || '—'}</span>} />
              <Field label="Status"    value={<StatusBadge status={status === 'active' ? 'active' : 'inactive'} label={status} />} />
              <Field label="Subscriber" value={subId ? <Link to={`/subscribers/${subId}`} className="text-blue-600 hover:underline">{account?.subscriberName || `#${subId}`}</Link> : null} />
              <Field label="Synced" value={account.syncedAt || account.synced_at
                ? fmtDateTime(account.syncedAt || account.synced_at)
                : null} />
              <Field label="Created" value={account.createdAt || account.created_at
                ? fmtDate(account.createdAt || account.created_at)
                : null} />
            </dl>

            <RevealedSecret revealed={revealSecret.revealed} onHide={revealSecret.hide} />
          </Section>

          {secretOpen && (
            <SetSipPasswordModal
              sipAccountId={id}
              username={ext}
              onClose={() => setSecretOpen(false)}
              onSaved={() => {
                setSecretOpen(false);
                queryClient.invalidateQueries({ queryKey: ['sip-account', id] });
              }}
            />
          )}

          <Section
            title="SIP Registration"
            subtitle="Live state pulled from the PBX agent. Refreshes every 30s while this tab is open."
          >
            {!agentId ? (
              <p className="mt-3 text-sm text-gray-400">This extension isn&rsquo;t bound to a reachable PBX host yet.</p>
            ) : sipStatusQ.isLoading ? (
              <p className="mt-3 text-sm text-gray-400">Loading…</p>
            ) : sipStatusQ.isError ? (
              <p className="mt-3 text-sm text-gray-400">
                Couldn&rsquo;t reach the PBX agent. Try again in a moment.
              </p>
            ) : !sipStatusQ.data ? (
              <p className="mt-3 text-sm text-gray-400">
                Agent returned no registration data yet.
              </p>
            ) : (() => {
              const s = sipStatusQ.data;
              const registered = Boolean(s.registered);
              return (
                <>
                  <div className="mt-3 flex items-center gap-2">
                    <StatusBadge
                      status={registered ? 'active' : 'critical'}
                      label={registered ? 'registered' : 'unregistered'}
                    />
                    <span className="text-xs text-gray-500">
                      as of {new Date().toLocaleTimeString()}
                    </span>
                  </div>
                  <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
                    <Field label="Endpoint state" value={s.state || null} />
                    <Field label="Active channels" value={s.channels ?? null} />
                    <Field label="Contact URI" value={s.contactUri ? <span className="font-mono text-xs">{s.contactUri}</span> : null} />
                    <Field label="Contact status" value={s.contactStatus || null} />
                    <Field label="Round-trip" value={s.contactRttMs != null ? `${s.contactRttMs.toFixed(1)} ms` : null} />
                    <Field label="Caller ID" value={s.callerid || null} />
                  </dl>
                </>
              );
            })()}
          </Section>
        </>
      )}

      {/* Forwarding tab */}
      {activeTab === 'forwarding' && (
        <Section title="Forwarding" subtitle="Call-forwarding rules on the PBX for this extension.">
          {!agentId ? (
            <p className="mt-3 text-sm text-gray-400">No PBX host bound.</p>
          ) : fwdQ.isLoading ? (
            <p className="mt-3 text-sm text-gray-400">Loading…</p>
          ) : fwdQ.isError ? (
            <p className="mt-3 text-sm text-gray-400">Couldn&rsquo;t reach the PBX agent.</p>
          ) : !fwdQ.data ? (
            <p className="mt-3 text-sm text-gray-400">
              Forwarding endpoint isn&rsquo;t returning data yet — the agent may still be stubbing this handler.
            </p>
          ) : (() => {
            // Each rule arrives as { destination, enabled } from the agent —
            // render as text (never the raw object; that crashes React).
            const rule = (r) => {
              if (r == null) return null;
              if (typeof r === 'string') return r || null;
              if (typeof r === 'object') {
                if (!r.enabled) return <span className="text-gray-400">off</span>;
                return <span className="font-mono">{r.destination || '—'}</span>;
              }
              return String(r);
            };
            return (
              <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Unconditional" value={rule(fwdQ.data.unconditional ?? fwdQ.data.always)} />
                <Field label="Busy"          value={rule(fwdQ.data.busy)} />
                <Field label="No answer"     value={rule(fwdQ.data.noAnswer ?? fwdQ.data.no_answer)} />
                <Field label="Unavailable"   value={rule(fwdQ.data.unavailable)} />
              </dl>
            );
          })()}
        </Section>
      )}

      {/* Voicemail tab — settings form (the endpoint returns the astdb-backed
          settings blob, not messages). */}
      {activeTab === 'voicemail' && (
        <Section title="Voicemail" subtitle="Voicemail settings for this extension.">
          {/* Without a mailbox there is nothing for these settings to apply to,
              and the form would otherwise look like a working voicemail config.
              Enabling is an explicit action, never a side effect — which is why
              nothing on this page does it implicitly. */}
          {vmState === 'off' && (
            <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <span className="font-medium">This extension has no mailbox on the PBX.</span>{' '}
              Callers can&rsquo;t leave a message and the settings below apply to nothing. Voicemail is
              switched on per extension — turn it on from the subscriber&rsquo;s Voicemail tab if this
              extension should have one.
            </p>
          )}
          {vmState === 'unknown' && (
            <p className="mb-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
              Couldn&rsquo;t read the mailbox state from the PBX just now, so it isn&rsquo;t clear whether
              this extension has one.
            </p>
          )}
          {!agentId ? (
            <p className="mt-3 text-sm text-gray-400">No PBX host bound.</p>
          ) : vmQ.isLoading ? (
            <p className="mt-3 text-sm text-gray-400">Loading…</p>
          ) : vmQ.isError ? (
            <p className="mt-3 text-sm text-gray-400">Couldn&rsquo;t reach the PBX agent.</p>
          ) : !vmQ.data ? (
            <p className="mt-3 text-sm text-gray-400">Agent hasn&rsquo;t returned voicemail data yet.</p>
          ) : (
            <VoicemailSettings
              vm={vmQ.data}
              agentId={agentId}
              ext={ext}
              canWrite={canWriteVm}
              onSaved={() => queryClient.invalidateQueries({ queryKey: ['ext', id, 'voicemail'] })}
            />
          )}
        </Section>
      )}

      {/* Call History tab */}
      {activeTab === 'callhistory' && (
        <Section
          title="Call History"
          subtitle={(() => {
            const list = Array.isArray(callsQ.data) ? callsQ.data
                       : Array.isArray(callsQ.data?.calls) ? callsQ.data.calls
                       : Array.isArray(callsQ.data?.rows) ? callsQ.data.rows
                       : [];
            const win = CALL_WINDOWS.find((w) => w.id === callWindow)?.label || '';
            return list.length === 0
              ? `No calls recorded in the last ${win}.`
              : `${list.length} call${list.length === 1 ? '' : 's'} in the last ${win}.`;
          })()}
          actions={
            <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5 text-xs">
              {CALL_WINDOWS.map((w) => (
                <button
                  key={w.id}
                  onClick={() => setCallWindow(w.id)}
                  className={`rounded px-2.5 py-1 font-medium transition-colors ${
                    callWindow === w.id
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {w.label}
                </button>
              ))}
            </div>
          }
        >
          {!agentId ? (
            <p className="mt-3 text-sm text-gray-400">No PBX host bound.</p>
          ) : callsQ.isLoading ? (
            <p className="mt-3 text-sm text-gray-400">Loading…</p>
          ) : callsQ.isError ? (
            <p className="mt-3 text-sm text-gray-400">Couldn&rsquo;t reach the PBX agent.</p>
          ) : (() => {
            const rows = Array.isArray(callsQ.data) ? callsQ.data
                       : Array.isArray(callsQ.data?.calls) ? callsQ.data.calls
                       : Array.isArray(callsQ.data?.rows) ? callsQ.data.rows
                       : [];
            if (rows.length === 0) return null;
            return (
              <div className="mt-3 overflow-hidden rounded-md border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs text-gray-500">
                    <tr>
                      <th className="px-3 py-2">When</th>
                      <th className="px-3 py-2">Direction</th>
                      <th className="px-3 py-2">From</th>
                      <th className="px-3 py-2">To</th>
                      <th className="px-3 py-2">Duration</th>
                      <th className="px-3 py-2">Result</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((c) => {
                      const ts = c.ts ?? c.start_ts;
                      const secs = c.billsec ?? c.duration ?? 0;
                      const kind = c.direction || c.kind;
                      const disp = String(c.disposition || '').toUpperCase();
                      const dispCls = disp === 'ANSWERED' ? 'bg-green-100 text-green-700'
                                    : disp === 'FAILED'   ? 'bg-red-100 text-red-700'
                                    : 'bg-gray-100 text-gray-600';
                      const kindCls = kind === 'inbound'  ? 'bg-green-100 text-green-700'
                                    : kind === 'outbound' ? 'bg-blue-100 text-blue-700'
                                    : 'bg-gray-100 text-gray-600';
                      return (
                        <tr key={`${c.uniqueid || c.id}-${ts}`}>
                          <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                            {ts ? new Date(ts * 1000).toLocaleString([], {
                              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                            }) : '—'}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${kindCls}`}>{kind || 'other'}</span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{c.src || c.from || '—'}</td>
                          <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{c.dst || c.to || '—'}</td>
                          <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{fmtDuration(secs)}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${dispCls}`}>
                              {c.disposition ? String(c.disposition).toLowerCase() : 'unknown'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </Section>
      )}
    </div>
  );
}
